import React, { useState } from 'react';
import { Modal, Form, Input, Button, Typography, Space } from 'antd';
import { useToast } from '../../contexts/ToastContext';
import { SendOutlined } from '@ant-design/icons';
import { apiService } from '../../services/api';

const { TextArea } = Input;
const { Title, Text } = Typography;

interface ReferenceRequestModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  application: {
    id: number;
    name: string;
    type: number;
    mentorId: number;
    menteeId: number;
  } | null;
  userType: 'mentee' | 'mentor';
}

const ReferenceRequestModal: React.FC<ReferenceRequestModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  application,
  userType
}) => {
  const toast = useToast();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values: { content: string }) => {
    if (!application) return;

    setLoading(true);
    try {
      const requestData = {
        content: values.content,
        type: application.type,
        applicationId: application.id,
        targetUserId: userType === 'mentee' ? application.mentorId : application.menteeId
      };

      let response;
      if (userType === 'mentee') {
        response = await apiService.createMenteeReferenceRequest(requestData);
      } else {
        response = await apiService.createMentorReferenceRequest(requestData);
      }

      if (response.success) {
        toast.success('Referans talebi başarıyla gönderildi!');
        form.resetFields();
        onSuccess();
        onCancel();
      } else {
        toast.error(response.message || 'Referans talebi gönderilemedi.');
      }
    } catch (error) {
      console.error('Referans talebi gönderilirken hata:', error);
      toast.error('Referans talebi gönderilirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const getTypeText = (type: number) => {
    return type === 0 ? '1-1 Görüşme' : 'Bootcamp';
  };

  const getTypeColor = (type: number) => {
    return type === 0 ? 'var(--primary-500)' : 'var(--success)';
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <SendOutlined style={{ marginRight: '8px', color: 'var(--primary-500)' }} />
          <span>Referans Talebi Gönder</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      {application && (
        <div>
          <div style={{ marginBottom: '24px', padding: '16px', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
            <Title level={5} style={{ margin: 0, marginBottom: '8px' }}>
              {application.name}
            </Title>
            <Text type="secondary">
              Tip: <span style={{ color: getTypeColor(application.type), fontWeight: 'bold' }}>
                {getTypeText(application.type)}
              </span>
            </Text>
            <br />
            <Text type="secondary">
              {userType === 'mentee' ? 'Mentor ID' : 'Mentee ID'}: #{userType === 'mentee' ? application.mentorId : application.menteeId}
            </Text>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Form.Item
              name="content"
              label="Referans Talebi İçeriği"
              rules={[
                { required: true, message: 'Lütfen referans talebi içeriğini giriniz.' },
                { min: 10, message: 'En az 10 karakter giriniz.' },
                { max: 1000, message: 'En fazla 1000 karakter girebilirsiniz.' }
              ]}
            >
              <TextArea
                rows={6}
                placeholder="Referans talebinizi detaylı bir şekilde yazınız..."
                showCount
                maxLength={1000}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={onCancel}>
                  İptal
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={loading}
                  icon={<SendOutlined />}
                >
                  Referans Talebi Gönder
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </div>
      )}
    </Modal>
  );
};

export default ReferenceRequestModal;
